import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import {
  IEsimReservationFilterPackage,
  IEsimReservationFilterZone
} from "../../../interfaces/esim/EsimReservations.types";
import { getEsimReservationFilterOptions } from "../../../services/Esim.service";
import FilterOptionList from "../../reservation_v2/filters/FilterOptionList";
import Input from "../../ui/input/Input";

const loadingRow = <p className="px-2 py-1.5 text-sm text-neutral">Duke u ngarkuar...</p>;
const note = (text: string) => <p className="px-2 py-1.5 text-sm text-neutral">{text}</p>;

// One request for both chips: every zone with all of its packages. Kept for the page's lifetime,
// since zones and packages change rarely; a failed load is retried on the next open.
let zonesRequest: Promise<IEsimReservationFilterZone[]> | null = null;
const loadZones = () => {
  zonesRequest ??= getEsimReservationFilterOptions()
    .then((res) => res.data?.zones || [])
    .catch((error) => {
      zonesRequest = null;
      throw error;
    });
  return zonesRequest;
};

const useZones = () => {
  const [zones, setZones] = useState<IEsimReservationFilterZone[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    loadZones()
      .then((loaded) => !cancelled && setZones(loaded))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, []);
  return { zones, failed };
};

// Every package of a zone is named after the zone, so a package is told apart by its data and
// validity, as on the eSIM dashboard's package picker.
export const packageLabel = (pkg: IEsimReservationFilterPackage) =>
  `${pkg.isUnlimited ? "Unlimited" : `${pkg.totalGB} GB`} · ${pkg.validityDays} ditë`;

// The zones, narrowed as the user types.
export const ZoneList = ({ selected, onPick }: {
  selected: string; onPick: (zone: IEsimReservationFilterZone) => void;
}) => {
  const { zones, failed } = useZones();
  const [search, setSearch] = useState("");

  const options = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (zones || [])
      .filter((zone) => !term || zone.name.toLowerCase().includes(term))
      .map((zone) => ({ label: zone.name, value: `${zone.id}` }));
  }, [zones, search]);

  if (failed) return note("Zonat nuk u ngarkuan. Mbylle dhe provo përsëri.");
  return (
    <div className="flex flex-col gap-2">
      <Input
        placeholder="Kërko zonën"
        paddingType="fill"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        beforeClassName="text-neutral"
        before={<MagnifyingGlassIcon className="w-4 h-4" strokeWidth={2} />}
      />
      <div className="max-h-64 overflow-y-auto">
        {zones === null ? loadingRow : (
          <FilterOptionList
            options={options}
            selected={[selected]}
            onToggle={(value) => {
              const zone = zones.find((z) => `${z.id}` === value);
              if (zone) onPick(zone);
            }}
          />
        )}
        {zones !== null && !options.length && note("Asnjë zonë.")}
      </div>
    </div>
  );
};

// Every package of the chosen zone, smallest first; ones no longer sold are marked, not hidden,
// so their past reservations can still be found.
export const PackageList = ({ zoneId, selected, onPick }: {
  zoneId: string; selected: string; onPick: (pkg: IEsimReservationFilterPackage) => void;
}) => {
  const { zones, failed } = useZones();

  if (failed) return note("Paketat nuk u ngarkuan. Mbylle dhe provo përsëri.");
  if (zones === null) return loadingRow;
  const packages = zones.find((zone) => `${zone.id}` === zoneId)?.packages || [];
  if (!packages.length) return note("Asnjë paketë.");
  return (
    <div className="max-h-64 overflow-y-auto">
      <FilterOptionList
        options={packages.map((pkg) => ({
          label: `${packageLabel(pkg)} · €${pkg.cost.toFixed(2)}${pkg.deleted ? " (jo më në shitje)" : ""}`,
          value: `${pkg.id}`
        }))}
        selected={[selected]}
        onToggle={(value) => {
          const pkg = packages.find((p) => `${p.id}` === value);
          if (pkg) onPick(pkg);
        }}
      />
    </div>
  );
};
